/**
 * Product Info Tabs Component
 * Handles tab navigation and content switching
 */
class ProductInfoTabs {
  constructor(container) {
    this.container = container;
    this.tabsNav = container.querySelector('.product-info-tabs__nav');
    this.tabButtons = container.querySelectorAll('.product-info-tabs__nav-button');
    this.tabPanels = container.querySelectorAll('.product-info-tabs__panel');
    this.scrollIndicatorLeft = container.querySelector('.product-info-tabs__scroll-indicator--left');
    this.scrollIndicatorRight = container.querySelector('.product-info-tabs__scroll-indicator--right');
    
    if (!this.tabsNav || this.tabButtons.length === 0) {
      return;
    }

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupKeyboardNavigation();
    this.setupScrollIndicator();
  }

  setupEventListeners() {
    // Handle tab button clicks
    this.tabButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const targetTabId = button.getAttribute('data-tab-target');
        if (targetTabId) {
          this.switchTab(targetTabId, button);
        }
      });
    });
  }

  setupKeyboardNavigation() {
    // Handle keyboard navigation (Arrow keys, Home, End)
    this.tabsNav.addEventListener('keydown', (e) => {
      const currentIndex = Array.from(this.tabButtons).findIndex(
        (btn) => btn.classList.contains('product-info-tabs__nav-button--active')
      );

      let targetIndex = currentIndex;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          targetIndex = currentIndex > 0 ? currentIndex - 1 : this.tabButtons.length - 1;
          break;
        case 'ArrowRight':
          e.preventDefault();
          targetIndex = currentIndex < this.tabButtons.length - 1 ? currentIndex + 1 : 0;
          break;
        case 'Home':
          e.preventDefault();
          targetIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          targetIndex = this.tabButtons.length - 1;
          break;
        default:
          return;
      }

      if (targetIndex !== currentIndex) {
        const targetButton = this.tabButtons[targetIndex];
        const targetTabId = targetButton.getAttribute('data-tab-target');
        this.switchTab(targetTabId, targetButton);
        targetButton.focus();
      }
    });
  }

  setupScrollIndicator() {
    if ((!this.scrollIndicatorLeft && !this.scrollIndicatorRight) || !this.tabsNav) {
      return;
    }

    // Only show and enable scroll indicators on mobile
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 767;
      if (!isMobile) {
        // Hide both indicators on desktop
        if (this.scrollIndicatorLeft) {
          this.scrollIndicatorLeft.style.display = 'none';
        }
        if (this.scrollIndicatorRight) {
          this.scrollIndicatorRight.style.display = 'none';
        }
        return;
      }

      // Check if there's scrollable content
      const hasOverflow = this.tabsNav.scrollWidth > this.tabsNav.clientWidth;
      if (!hasOverflow) {
        // Hide both indicators if no overflow
        if (this.scrollIndicatorLeft) {
          this.scrollIndicatorLeft.style.display = 'none';
        }
        if (this.scrollIndicatorRight) {
          this.scrollIndicatorRight.style.display = 'none';
        }
        return;
      }

      // Check scroll position
      const scrollLeft = this.tabsNav.scrollLeft;
      const scrollWidth = this.tabsNav.scrollWidth;
      const clientWidth = this.tabsNav.clientWidth;
      const isScrolledToStart = scrollLeft <= 1;
      const isScrolledToEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      // Show/hide left indicator (show when scrolled right)
      if (this.scrollIndicatorLeft) {
        if (isScrolledToStart) {
          this.scrollIndicatorLeft.style.display = 'none';
        } else {
          this.scrollIndicatorLeft.style.display = 'flex';
        }
      }

      // Show/hide right indicator (show when not at end)
      if (this.scrollIndicatorRight) {
        if (isScrolledToEnd) {
          this.scrollIndicatorRight.style.display = 'none';
        } else {
          this.scrollIndicatorRight.style.display = 'flex';
        }
      }
    };

    // Check on load and resize
    checkMobile();
    window.addEventListener('resize', checkMobile);
    this.tabsNav.addEventListener('scroll', checkMobile);

    // Handle click on left scroll indicator (scroll left)
    if (this.scrollIndicatorLeft) {
      this.scrollIndicatorLeft.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Calculate scroll amount (scroll by approximately one tab width to the left)
        const firstTab = this.tabButtons[0];
        if (firstTab) {
          const tabWidth = firstTab.offsetWidth;
          const scrollAmount = Math.max(0, this.tabsNav.scrollLeft - tabWidth);
          
          this.tabsNav.scrollTo({
            left: scrollAmount,
            behavior: 'smooth'
          });
        }
      });
    }

    // Handle click on right scroll indicator (scroll right)
    if (this.scrollIndicatorRight) {
      this.scrollIndicatorRight.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Calculate scroll amount (scroll by approximately one tab width to the right)
        const firstTab = this.tabButtons[0];
        if (firstTab) {
          const tabWidth = firstTab.offsetWidth;
          const scrollAmount = this.tabsNav.scrollLeft + tabWidth;
          
          this.tabsNav.scrollTo({
            left: scrollAmount,
            behavior: 'smooth'
          });
        }
      });
    }
  }

  switchTab(targetTabId, activeButton) {
    // Remove active state from all buttons and panels
    this.tabButtons.forEach((btn) => {
      btn.classList.remove('product-info-tabs__nav-button--active');
      btn.setAttribute('aria-selected', 'false');
    });

    this.tabPanels.forEach((panel) => {
      panel.classList.remove('product-info-tabs__panel--active');
    });

    // Add active state to selected button and panel
    if (activeButton) {
      activeButton.classList.add('product-info-tabs__nav-button--active');
      activeButton.setAttribute('aria-selected', 'true');
    }

    const targetPanel = this.container.querySelector(`#${targetTabId}`);
    if (targetPanel) {
      targetPanel.classList.add('product-info-tabs__panel--active');
      
      // Trigger custom event for potential integrations
      this.container.dispatchEvent(
        new CustomEvent('productInfoTabChanged', {
          detail: {
            tabId: targetTabId,
            button: activeButton,
            panel: targetPanel
          }
        })
      );
    }
  }

  // Public method to programmatically switch tabs
  switchToTab(tabId) {
    const button = this.container.querySelector(`[data-tab-target="${tabId}"]`);
    if (button) {
      this.switchTab(tabId, button);
    }
  }

  // Public method to get active tab ID
  getActiveTabId() {
    const activeButton = this.container.querySelector('.product-info-tabs__nav-button--active');
    return activeButton ? activeButton.getAttribute('data-tab-target') : null;
  }
}

/**
 * Read More/Less Component
 * Handles expanding and collapsing text content by finding "read more" in the text
 */
class ReadMoreWordCount {
  constructor(textElement, wordLimit = 100) {
    this.textElement = textElement;
    this.wordLimit = wordLimit;
    this.originalHTML = textElement.innerHTML;
    this.isExpanded = false;
    this.isInitialLoad = true; // Track if this is the initial load
    this.init();
  }

  countWords(html) {
    // Create a temporary div to parse HTML and count words
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    const words = textContent.trim().split(/\s+/).filter(word => word.length > 0);
    return words.length;
  }

  truncateByWords(html, wordLimit) {
    const wordCount = this.countWords(html);
    
    if (wordCount <= wordLimit) {
      return { truncated: html, needsTruncation: false };
    }

    // Create a temporary container to work with the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Walk through DOM nodes and truncate text nodes
    let currentWordCount = 0;
    
    const truncateNode = (node) => {
      if (currentWordCount >= wordLimit) {
        return false; // Stop processing
      }
      
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        const words = text.trim().split(/\s+/).filter(w => w.length > 0);
        const newCount = currentWordCount + words.length;
        
        if (newCount <= wordLimit) {
          currentWordCount = newCount;
          return true; // Keep this node
        } else {
          // Truncate this text node
          const remaining = wordLimit - currentWordCount;
          if (remaining > 0) {
            const truncatedWords = words.slice(0, remaining);
            // Preserve leading/trailing whitespace
            const leadingSpace = text.match(/^\s*/)?.[0] || '';
            const trailingSpace = text.match(/\s*$/)?.[0] || '';
            node.textContent = leadingSpace + truncatedWords.join(' ') + trailingSpace;
            currentWordCount = wordLimit;
            return true;
          }
          return false; // Don't keep this node
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Process children first
        const children = Array.from(node.childNodes);
        const nodesToRemove = [];
        
        for (let i = 0; i < children.length; i++) {
          const child = children[i];
          if (!truncateNode(child)) {
            // Mark for removal
            nodesToRemove.push(child);
          }
        }
        
        // Remove nodes that exceeded word limit
        nodesToRemove.forEach(n => n.remove());
        
        // If element has no content, it will be removed by parent
        return node.textContent.trim().length > 0;
      }
      
      return true;
    };
    
    // Process all child nodes
    const children = Array.from(tempDiv.childNodes);
    const nodesToRemove = [];
    
    for (let child of children) {
      if (!truncateNode(child)) {
        nodesToRemove.push(child);
      }
    }
    
    // Remove nodes that exceeded word limit
    nodesToRemove.forEach(n => n.remove());
    
    return { truncated: tempDiv.innerHTML, needsTruncation: true };
  }

  init() {
    setTimeout(() => {
      const wordCount = this.countWords(this.originalHTML);
      
      if (wordCount <= this.wordLimit) {
        return; // No truncation needed
      }

      // Truncate content
      const { truncated, needsTruncation } = this.truncateByWords(this.originalHTML, this.wordLimit);
      
      if (!needsTruncation) {
        return;
      }
      
      this.truncatedHTML = truncated;
      
      // Initial collapsed state (without scrolling)
      this.collapse(true);
    }, 100);
  }

  collapse(skipScroll = false) {
    // Smooth transition: fade out, update content, fade in
    this.textElement.style.opacity = '0';
    this.textElement.style.transform = 'translateY(5px)';
    
    setTimeout(() => {
      // Append read more link inline after truncated content
      const readMoreHTML = '<span class="product-info-tabs__read-more-inline"><span class="product-info-tabs__ellipsis">...</span><a href="#" class="product-info-tabs__read-more-text">READ MORE</a></span>';
      
      // Find the last space in truncated HTML to insert read more link
      const lastSpaceIndex = this.truncatedHTML.lastIndexOf(' ');
      const beforeReadMore = lastSpaceIndex > 0 ? this.truncatedHTML.substring(0, lastSpaceIndex) : this.truncatedHTML;
      
      // Update content while invisible
      this.textElement.innerHTML = beforeReadMore + ' ' + readMoreHTML;
      this.textElement.classList.add('product-info-tabs__text--collapsed');
      
      // Fade in smoothly
      requestAnimationFrame(() => {
        this.textElement.style.opacity = '1';
        this.textElement.style.transform = 'translateY(0)';
        
        // Scroll to top of section after collapse (only if not skipped)
        if (!skipScroll) {
          setTimeout(() => {
            // Find the section container
            const sectionContainer = this.textElement.closest('.section-container');
            const targetElement = sectionContainer || this.textElement;
            
            if (targetElement) {
              // Calculate offset based on screen size
              const isMobile = window.innerWidth <= 767;
              const offset = isMobile ? 100 : 128;
              
              // Get element position
              const elementTop = targetElement.getBoundingClientRect().top + window.pageYOffset;
              
              // Scroll with offset
              window.scrollTo({
                top: elementTop - offset,
                behavior: 'smooth'
              });
            }
          }, 50);
        }
      });
      
      // Attach event listener to read more link
      const readMoreLink = this.textElement.querySelector('.product-info-tabs__read-more-text');
      if (readMoreLink) {
        readMoreLink.addEventListener('click', (e) => {
          e.preventDefault();
          this.expand();
        });
      }
      
      // Mark that initial load is complete
      this.isInitialLoad = false;
    }, 150);
    
    this.isExpanded = false;
  }

  expand() {
    // Smooth transition: fade out, update content, fade in
    this.textElement.style.opacity = '0';
    this.textElement.style.transform = 'translateY(-5px)';
    
    setTimeout(() => {
      // Show full content with read less link
      const readLessHTML = '<a href="#" class="product-info-tabs__read-less-text">READ LESS</a>';
      
      // Update content while invisible
      this.textElement.innerHTML = this.originalHTML + ' ' + readLessHTML;
      this.textElement.classList.remove('product-info-tabs__text--collapsed');
      
      // Fade in smoothly
      requestAnimationFrame(() => {
        this.textElement.style.opacity = '1';
        this.textElement.style.transform = 'translateY(0)';
      });
      
      // Attach event listener to read less link
      const readLessLink = this.textElement.querySelector('.product-info-tabs__read-less-text');
      if (readLessLink) {
        readLessLink.addEventListener('click', (e) => {
          e.preventDefault();
          this.collapse(false); // Allow scroll when user clicks
        });
      }
    }, 150);
    
    this.isExpanded = true;
  }
}

class ReadMoreLess {
  constructor(textWrapper) {
    this.wrapper = textWrapper;
    this.textElement = textWrapper.querySelector('.product-info-tabs__text');
    this.readMoreLink = textWrapper.querySelector('.product-info-tabs__read-more-link');
    this.readMoreText = textWrapper.querySelector('.product-info-tabs__read-more-text');
    this.readLessText = textWrapper.querySelector('.product-info-tabs__read-less-text');
    this.ellipsis = textWrapper.querySelector('.product-info-tabs__ellipsis');
    this.isExpanded = false;
    this.originalHTML = this.textElement.innerHTML;
    this.hiddenContent = null;
    this.isInitialLoad = true; // Track if this is the initial load
    
    this.init();
  }

  init() {
    // Wait for content to render
    setTimeout(() => {
      // Find "read more" in the HTML content (case insensitive)
      const readMoreRegex = /(read\s+more)/i;
      const match = this.originalHTML.match(readMoreRegex);
      
      if (!match) {
        // No "read more" found, hide the read more link
        if (this.readMoreLink) {
          this.readMoreLink.style.display = 'none';
        }
        return;
      }

      // Find the position of "read more" in the HTML
      const readMoreIndex = this.originalHTML.toLowerCase().indexOf('read more');
      
      if (readMoreIndex === -1) {
        // Couldn't find it, hide the link
        if (this.readMoreLink) {
          this.readMoreLink.style.display = 'none';
        }
        return;
      }

      // Split content at "read more"
      // We need to find where "read more" ends in the HTML
      const beforeReadMore = this.originalHTML.substring(0, readMoreIndex);
      
      // Find the end of "read more" - could be followed by various things
      let afterReadMoreStart = readMoreIndex;
      const readMoreEndMatch = this.originalHTML.substring(readMoreIndex).match(/read\s+more\s*/i);
      if (readMoreEndMatch) {
        afterReadMoreStart = readMoreIndex + readMoreEndMatch[0].length;
      }
      
      const afterReadMore = this.originalHTML.substring(afterReadMoreStart);
      
      // Store the hidden content
      this.hiddenContent = afterReadMore.trim();
      
      // Set initial collapsed state (show only content before "read more") - skip scroll on init
      this.collapse(true);
      
      // Add click event listeners
      if (this.readMoreText) {
        this.readMoreText.addEventListener('click', (e) => {
          e.preventDefault();
          this.expand();
        });
      }
      
      if (this.readLessText) {
        this.readLessText.addEventListener('click', (e) => {
          e.preventDefault();
          this.collapse(false); // Allow scroll when user clicks
        });
      }
    }, 100);
  }

  collapse(skipScroll = false) {
    // Smooth transition: fade out, update content, fade in
    this.textElement.style.opacity = '0';
    this.textElement.style.transform = 'translateY(5px)';
    
    setTimeout(() => {
      // Find "read more" and replace it with "...READ MORE" link inline
      const readMoreIndex = this.originalHTML.toLowerCase().indexOf('read more');
      if (readMoreIndex !== -1) {
        // Find the actual "read more" text (preserving case)
        const beforeReadMore = this.originalHTML.substring(0, readMoreIndex);
        const readMoreMatch = this.originalHTML.substring(readMoreIndex).match(/read\s+more/i);
        
        if (readMoreMatch) {
          // Create inline replacement with "...READ MORE" link
          // Only show content before "read more", hide everything after
          const replacement = '<span class="product-info-tabs__read-more-inline"><span class="product-info-tabs__ellipsis">...</span><a href="#" class="product-info-tabs__read-more-text">READ MORE</a></span>';
          
          const modifiedHTML = beforeReadMore + replacement;
          
          // Update content while invisible
          this.textElement.innerHTML = modifiedHTML;
          this.textElement.classList.add('product-info-tabs__text--collapsed');
          
          // Fade in smoothly
          requestAnimationFrame(() => {
            this.textElement.style.opacity = '1';
            this.textElement.style.transform = 'translateY(0)';
            
            // Scroll to top of section after collapse (only if not initial load and not skipped)
            if (!skipScroll && !this.isInitialLoad) {
              setTimeout(() => {
                // Find the section container
                const sectionContainer = this.textElement.closest('.section-container');
                const targetElement = sectionContainer || this.textElement;
                
                if (targetElement) {
                  // Calculate offset based on screen size
                  const isMobile = window.innerWidth <= 767;
                  const offset = isMobile ? 100 : 168;
                  
                  // Get element position
                  const elementTop = targetElement.getBoundingClientRect().top + window.pageYOffset;
                  
                  // Scroll with offset
                  window.scrollTo({
                    top: elementTop - offset,
                    behavior: 'smooth'
                  });
                }
              }, 50);
            }
          });
          
          // Hide the separate read more link (we're using inline one)
          if (this.readMoreLink) {
            this.readMoreLink.style.display = 'none';
          }
          
          // Add click listener to inline read more link
          const inlineReadMore = this.textElement.querySelector('.product-info-tabs__read-more-inline .product-info-tabs__read-more-text');
          if (inlineReadMore) {
            // Remove old listener if exists and add new one
            const newInlineReadMore = inlineReadMore.cloneNode(true);
            inlineReadMore.parentNode.replaceChild(newInlineReadMore, inlineReadMore);
            newInlineReadMore.addEventListener('click', (e) => {
              e.preventDefault();
              this.expand();
            });
          }
        }
      }
      
      // Mark that initial load is complete
      this.isInitialLoad = false;
    }, 150);
    
    this.isExpanded = false;
  }

  expand() {
    // Smooth transition: fade out, update content, fade in
    this.textElement.style.opacity = '0';
    this.textElement.style.transform = 'translateY(-5px)';
    
    setTimeout(() => {
      // Show full content but remove "read more" text (it's just an identifier)
      let expandedHTML = this.originalHTML;
      
      // Remove "read more" text (case insensitive, preserving spacing)
      const readMoreRegex = /read\s+more/gi;
      expandedHTML = expandedHTML.replace(readMoreRegex, '');
      
      // Clean up any extra spaces that might be left
      expandedHTML = expandedHTML.replace(/\s+/g, ' ').trim();
      
      // Update content while invisible
      this.textElement.innerHTML = expandedHTML;
      this.textElement.classList.remove('product-info-tabs__text--collapsed');
      
      // Fade in smoothly
      requestAnimationFrame(() => {
        this.textElement.style.opacity = '1';
        this.textElement.style.transform = 'translateY(0)';
      });
      
      // Show the read less link
      if (this.readMoreLink) {
        this.readMoreLink.style.display = 'inline';
      }
      if (this.readMoreText) {
        this.readMoreText.style.display = 'none';
      }
      if (this.readLessText) {
        this.readLessText.style.display = 'inline';
      }
      if (this.ellipsis) {
        this.ellipsis.style.display = 'none';
      }
    }, 150);
    
    this.isExpanded = true;
  }
}

/**
 * Compare Models Dropdown Component
 * Handles dropdown selection for product comparison cards
 */
class CompareModelsDropdown {
  constructor(container) {
    this.container = container;
    this.productsData = [];
    this.cards = container.querySelectorAll('.compare-model-card');
    
    this.init();
  }

  init() {
    // Parse products data from JSON script
    const dataScript = this.container.querySelector('[data-compare-products-data]');
    if (dataScript) {
      try {
        this.productsData = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('Error parsing compare products data:', e);
        return;
      }
    }

    if (this.productsData.length === 0) return;

    // Setup event listeners for each card
    this.cards.forEach((card) => {
      // Only setup dropdown for cards that have dropdown functionality
      if (card.hasAttribute('data-compare-card')) {
        this.setupCardDropdown(card);
      }
      // Setup selection for all cards
      this.setupCardSelection(card);
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.compare-model-card')) {
        this.closeAllDropdowns();
      }
    });
  }

  setupCardSelection(card) {
    // Add click handler to card (but not on dropdown elements)
    card.addEventListener('click', (e) => {
      // Don't select if clicking on dropdown trigger, dropdown menu, or dropdown options
      if (e.target.closest('[data-dropdown-trigger]') || 
          e.target.closest('[data-dropdown-menu]') || 
          e.target.closest('[data-dropdown-option]')) {
        return;
      }

      // Remove selected class from all cards
      const allCards = this.container.querySelectorAll('.compare-model-card');
      allCards.forEach((c) => {
        c.classList.remove('compare-model-card--selected');
      });

      // Add selected class to clicked card
      card.classList.add('compare-model-card--selected');
    });
  }

  setupCardDropdown(card) {
    const trigger = card.querySelector('[data-dropdown-trigger]');
    const menu = card.querySelector('[data-dropdown-menu]');
    const closeBtn = card.querySelector('[data-dropdown-close]');
    const options = card.querySelectorAll('[data-dropdown-option]');

    if (!trigger || !menu) return;

    // Toggle dropdown on header click
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = card.classList.contains('dropdown-open');
      
      // Close all other dropdowns first
      this.closeAllDropdowns();
      
      if (!isOpen) {
        card.classList.add('dropdown-open');
        
        // Make this card selected (add border) when dropdown opens
        const allCards = this.container.querySelectorAll('.compare-model-card');
        allCards.forEach((c) => {
          c.classList.remove('compare-model-card--selected');
        });
        card.classList.add('compare-model-card--selected');
      }
    });

    // Close dropdown on close button click
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        card.classList.remove('dropdown-open');
      });
    }

    // Handle option selection
    options.forEach((option) => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();
        const productId = option.getAttribute('data-product-id');
        this.selectProduct(card, productId);
        card.classList.remove('dropdown-open');
      });
    });
  }

  closeAllDropdowns() {
    this.cards.forEach((card) => {
      card.classList.remove('dropdown-open');
    });
  }

  selectProduct(card, productId) {
    // Find product data
    const product = this.productsData.find(p => p.id === productId);
    if (!product) return;

    // Update card's selected product ID
    card.setAttribute('data-selected-product-id', productId);

    // Update image
    const imageContainer = card.querySelector('.compare-model-card__image');
    if (imageContainer && product.image) {
      imageContainer.innerHTML = `<img src="${product.image}" alt="${product.title}" loading="lazy">`;
    }

    // Update title in header
    const headerTitle = card.querySelector('[data-dropdown-trigger] .compare-model-card__title');
    if (headerTitle) {
      headerTitle.textContent = product.title;
    }

    // Update title in dropdown header
    const dropdownHeaderTitle = card.querySelector('[data-dropdown-close] .compare-model-card__title');
    if (dropdownHeaderTitle) {
      dropdownHeaderTitle.textContent = product.title;
    }

    // Update selected state in dropdown options
    const options = card.querySelectorAll('[data-dropdown-option]');
    options.forEach((option) => {
      const optionProductId = option.getAttribute('data-product-id');
      if (optionProductId === productId) {
        option.classList.add('compare-model-card__dropdown-item--selected');
      } else {
        option.classList.remove('compare-model-card__dropdown-item--selected');
      }
    });

    // Update specs
    this.updateSpecs(card, product.specs);

    // Make this card selected (add border)
    const allCards = this.container.querySelectorAll('.compare-model-card');
    allCards.forEach((c) => {
      c.classList.remove('compare-model-card--selected');
    });
    card.classList.add('compare-model-card--selected');
  }

  updateSpecs(card, specs) {
    const specsContainer = card.querySelector('.compare-model-card__specs');
    if (!specsContainer || !specs) return;

    // Generate new specs HTML
    let specsHTML = '';
    specs.forEach((spec) => {
      const progress = spec.progress;
      specsHTML += `
        <div class="compare-model-spec">
          <span class="compare-model-spec__label">${spec.label}</span>
          <div class="compare-model-spec__track">
            <div class="compare-model-spec__progress" style="--progress: ${progress}%;"></div>
            <div class="compare-model-spec__track-dots">
              <span class="compare-model-spec__dot ${progress >= 0 ? 'compare-model-spec__dot--filled' : ''}"></span>
              <span class="compare-model-spec__dot ${progress >= 25 ? 'compare-model-spec__dot--filled' : ''}"></span>
              <span class="compare-model-spec__dot ${progress >= 50 ? 'compare-model-spec__dot--filled' : ''}"></span>
              <span class="compare-model-spec__dot ${progress >= 75 ? 'compare-model-spec__dot--filled' : ''}"></span>
              <span class="compare-model-spec__dot ${progress >= 100 ? 'compare-model-spec__dot--filled' : ''}"></span>
            </div>
            <div class="compare-model-spec__marker" style="--position: ${progress}%;"></div>
          </div>
          <div class="compare-model-spec__range">
            <span>${spec.min}</span>
            ${spec.mid ? `<span>${spec.mid}</span>` : ''}
            <span>${spec.max}</span>
          </div>
        </div>
      `;
    });

    specsContainer.innerHTML = specsHTML;
  }
}

// Initialize all Product Info Tabs instances on page load
document.addEventListener('DOMContentLoaded', () => {
  const tabsContainers = document.querySelectorAll('[data-product-info-tabs]');
  tabsContainers.forEach((container) => {
    new ProductInfoTabs(container);
    
    // Initialize Read More/Less for each description text (with "read more" identifier)
    const textWrappers = container.querySelectorAll('.product-info-tabs__text-wrapper[data-has-read-more="true"]');
    textWrappers.forEach((wrapper) => {
      new ReadMoreLess(wrapper);
    });
    
    // Initialize Read More/Less for word-count-based truncation (stick_height and shipping_return)
    const wordCountTexts = container.querySelectorAll('.product-info-tabs__text[data-word-count-truncate="true"]');
    wordCountTexts.forEach((textElement) => {
      new ReadMoreWordCount(textElement, 58);
    });
  });

  // Initialize Compare Models Dropdowns
  const compareModelsContainers = document.querySelectorAll('[data-compare-models]');
  compareModelsContainers.forEach((container) => {
    new CompareModelsDropdown(container);
  });
});

// Re-initialize when Shopify theme editor loads new sections
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.detail;
    const tabsContainer = section.querySelector('[data-product-info-tabs]');
    if (tabsContainer) {
      new ProductInfoTabs(tabsContainer);
      
      // Initialize Read More/Less for new sections (with "read more" identifier)
      const textWrappers = tabsContainer.querySelectorAll('.product-info-tabs__text-wrapper[data-has-read-more="true"]');
      textWrappers.forEach((wrapper) => {
        new ReadMoreLess(wrapper);
      });
      
      // Initialize Read More/Less for word-count-based truncation
      const wordCountTexts = tabsContainer.querySelectorAll('.product-info-tabs__text[data-word-count-truncate="true"]');
      wordCountTexts.forEach((textElement) => {
        new ReadMoreWordCount(textElement, 58);
      });
    }

    // Re-initialize Compare Models Dropdowns
    const compareModelsContainer = section.querySelector('[data-compare-models]');
    if (compareModelsContainer) {
      new CompareModelsDropdown(compareModelsContainer);
    }
  });
}

